import React, { useState, useMemo } from "react";
import {
  Grid,
  Button,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import {
  NavigateBefore,
  Save,
  RestartAlt,
  AccessTime,
  AutoAwesome,
} from "@mui/icons-material";
import {
  CustomFormLabel,
} from "../../../../components/formsComponents";
import {
  CustomTimePicker,
} from "../../../../components/formsComponents/CustomDatePicker";
import { usePaymentReminderConfig } from "../context/PaymentReminderConfig.context";

/**
 * Calculate next N run times for a daily cron at given hour:minute
 */
const getNextRuns = (hour: string, minute: string, count: number = 3): Date[] => {
  const runs: Date[] = [];
  const h = parseInt(hour) || 0;
  const m = parseInt(minute) || 0;

  const now = new Date();
  let candidate = new Date(now);
  candidate.setHours(h, m, 0, 0);

  // If today's time already passed, start from tomorrow
  if (candidate <= now) {
    candidate.setDate(candidate.getDate() + 1);
  }

  for (let i = 0; i < count; i++) {
    runs.push(new Date(candidate));
    candidate = new Date(candidate);
    candidate.setDate(candidate.getDate() + 1);
  }

  return runs;
};

export const Step3CronScheduler: React.FC = () => {
  const { formState, goPrev, handleSubmit, loading } =
    usePaymentReminderConfig();

  // Initialize from formState
  const cron = formState.cron_scheduler;
  const [hour, setHour] = useState(cron?.hour && cron.hour !== "*" ? cron.hour : "8");
  const [minute, setMinute] = useState(cron?.minute && cron.minute !== "*" ? cron.minute : "0");

  // Build cron payload
  const cronPayload = useMemo(() => ({
    second: "0",
    minute,
    hour,
    day: "*",
    month: "*",
    weekday: "*",
  }), [hour, minute]);

  const nextRuns = useMemo(() => getNextRuns(hour, minute, 3), [hour, minute]);

  // Handle time picker change
  const handleTimeChange = (timeString: any) => {
    if (timeString) {
      const [h, m] = timeString.split(":");
      setHour(h?.replace(/^0+/, "") || "0");
      setMinute(m?.replace(/^0+/, "") || "0");
    }
  };

  // Reset
  const handleReset = () => {
    setHour("8");
    setMinute("0");
  };

  // Save
  const handleSave = async () => {
    await handleSubmit({ cron_scheduler: cronPayload as any });
  };

  return (
    <Box sx={{ maxWidth: 550, margin: "auto" }}>
      <Grid container spacing={1}>
        {/* Info */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={600} color="text.secondary">
            This reminder will run every day at the selected time.
          </Typography>
        </Grid>

        {/* Time Picker */}
        <Grid item xs={12}>
          <CustomFormLabel>Time *</CustomFormLabel>
          <CustomTimePicker
            fullWidth
            value={`${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`}
            onChange={handleTimeChange}
          />
        </Grid>

        {/* LIVE PREVIEW */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "grey.50",
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <AutoAwesome fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                Live preview
              </Typography>
            </Stack>

            {nextRuns.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  NEXT {nextRuns.length} RUNS
                </Typography>
                {nextRuns.map((run, idx) => (
                  <Stack key={idx} direction="row" alignItems="center" gap={0.5} mt={0.5}>
                    <AccessTime sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {run.toLocaleString()}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            )}
           
          </Box>
        </Grid>

        {/* ACTIONS */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
            <Button
              variant="outlined"
              startIcon={<NavigateBefore />}
              onClick={goPrev}
              disabled={loading}
            >
              Back
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RestartAlt />}
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Configuration"}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
